import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import CapitalEventBannerType1 from '@di-shared/features/capital/CapitalEventBannerType1';
import CapitalFrame from '@di-shared/features/capital/CapitalFrame';
import {getHmcType} from '@di-shared/features/capital/capitalMessage';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore, {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {TabType} from '@di-shared/features/home/kr/HomeContents.kr';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {isEventDuration} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ThemeType} from '@shared/cci/features/const/environment';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';

/**
 * 현대캐피탈은 운영계가 2개의 계로 분리되어있음
 * 운영베타 -> 테스트용
 * 운영 -> 실제 운영
 * 따라서 isProduction 옵션에 따라 랜딩되는 페이지를 분리하고, 베타는 화이트리스트만 접근 가능하게끔 함
 * (TEST) 10/23 이벤트 페이지 연결 -> mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-second?hmcType=${브랜드구분}&cbno=${차대번호}
 *
 * (TEST) 10/23 이벤트 페이지 연결_beta -> preprod-mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-second?hmcType=${브랜드구분}&cbno=${차대번호}
 *
 * (TEST) 10/23 이벤트 페이지 연결_beta_R00004 -> preprod-mydata-app.hyundaicapital.com/mydata/main/R00004/index.html#/event/myasset-campaign-second?hmcType=${브랜드구분}&cbno=${차대번호}
 *
 */
const HCS_HOME = 'https://mydata-app.hyundaicapital.com/mydata/main/index.html#/myassets';
const HCS_BETA_HOME = 'https://preprod-mydata-app.hyundaicapital.com/mydata/main/index.html#/myassets';

export interface CapitalContentsProps {
  vin: string;
  isProduction: boolean;
}

function CapitalContents(props: CapitalContentsProps): JSX.Element {
  return (
    <Suspense>
      {/*// [2025-07-21:cy.seo] FIXME: 임시 이벤트 연결 -> 삭제할것 EVENT2 */}
      {!props.isProduction && (
        <div style={{background: 'white', color: 'black', padding: '10px'}}>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              const eventPage = `https://mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-second?hmcType=${getHmcType()}&cbno=${props.vin}`;
              WebviewUtils.openNewWebView({
                url: eventPage,
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) 10/23 이벤트 페이지 연결
          </button>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: `https://preprod-mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-second?hmcType=${getHmcType()}&cbno=${props.vin}`,
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) 10/23 이벤트 페이지 연결_beta
          </button>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: `https://preprod-mydata-app.hyundaicapital.com/mydata/main/R00004/index.html#/event/myasset-campaign-second?hmcType=${getHmcType()}&cbno=${props.vin}`,
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) 10/23 이벤트 페이지 연결_beta_R00004
          </button>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: window.location.origin + ROUTE_PATHS.CAPITAL_EVENT_1.getFullPath(props.vin),
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) 이벤트 시작
          </button>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: window.location.origin + ROUTE_PATHS.CAPITAL_BRIDGE.getFullPath(props.vin) + '?from=event',
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) 이벤트 시작 (from LMS)
          </button>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              WebviewUtils.openNewWebView({
                url: window.location.origin + ROUTE_PATHS.CAPITAL_BRIDGE.getFullPath('_VIN_') + '?from=event',
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            (TEST) 이벤트 시작 (치환자 미동작)
          </button>
          <button
            type="button"
            style={{display: 'block', padding: '10px', border: '1px solid #000'}}
            onClick={() => {
              WebviewUtils.showToast('이벤트 참여를 위해 앱 업데이트 또는 차량을 등록해주세요.');
              WebviewUtils.close();
            }}
          >
            (TEST) 치환자 미동작 에러 케이스
          </button>
        </div>
      )}
      <Inner {...props} />
    </Suspense>
  );
}

function Inner({vin, isProduction}: CapitalContentsProps) {
  const {getFeatureAgreed} = useAvailableComponentKr({vin});

  const isCapitalAllAgreed =
    getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT) && getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);

  return isCapitalAllAgreed ? (
    <Contents
      vin={vin}
      isProduction={isProduction}
    />
  ) : (
    <Onboarding vin={vin} />
  );
}

function Contents({vin, isProduction}: CapitalContentsProps) {
  const {t} = useInferableTranslation();
  const {checkCapitalVinWhitelist} = useFirebaseStore(({checkCapitalVinWhitelist}) => ({checkCapitalVinWhitelist}));
  const theme = useEnvironmentStore((state) => state.theme);
  const to = `${isProduction ? HCS_HOME : HCS_BETA_HOME}?isLightMode=${theme === ThemeType.LIGHT ? 'Y' : 'N'}&hmcType=${getHmcType()}`;

  return (
    <div
      className="tab_content car_maintenance"
      style={{overflowX: 'hidden'}}
    >
      {/*// [2025-09-04:cy.seo] FIXME: 이벤트 후 삭제 필요 EVENT2*/}
      {(checkCapitalVinWhitelist(vin) || isEventDuration()) && (
        <CapitalEventBannerType1
          key="finance"
          vin={vin}
          onClick={() => {
            emitEventLog({
              event: 'di_vehicle_finance',
              data_action: 'view banner',
              data_name: '똑똑한 내 차 관리 시원한 커피와 함께',
            });
          }}
        />
      )}
      <section
        className="section section_service_connect"
        id={`${TabType.Finance}_tabpanel`}
        aria-labelledby={`${TabType.Finance}_tab`}
      >
        <h2 className="blind">{t('T40154')}</h2>
        <div className="section_container_header">
          <h3 className="title_xsmall_b">
            {t('T40155')}
            {!isProduction && ' (beta)'}
          </h3>
          <div className="logo_area">
            <ImageSelector
              id={`fe--capital-short-logo`}
              alt={`현대캐피탈 logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo_light.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
              className="logo"
            />
          </div>
        </div>
      </section>
      {/* fixme 공공서비스 작업으로 인한 CSS 깨짐으로 인하여 마크업 구조 임시 대응 */}
      {/* 3.2.5.V-개발-DI-Platform/capital css 이슈 수정 */}
      <CapitalFrame
        vin={vin}
        to={to}
        isInitMounted={false}
      />
    </div>
  );
}

function Onboarding({vin}: Omit<CapitalContentsProps, 'isProduction'>): JSX.Element {
  const {t} = useInferableTranslation();
  const theme = useEnvironmentStore((state) => state.theme);
  const navigate = useNavigate();

  return (
    <section className="section section_service_connect">
      <div className="section_container_header">
        <h3 className="title_xsmall_b">{t('T40155')}</h3>
        <div className="logo_area">
          <ImageSelector
            alt={`현대캐피탈 logo`}
            kiaLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/hyundai_capital_logo.png')}
            width="106"
            height="20"
            className="logo"
          />
        </div>
      </div>
      <div className="section_container">
        <div className="section_heading">
          <span
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T40165')}
          </span>
          <p className="section_heading_desc">{t('T40164')}</p>
        </div>
        <div className="img_box">
          <ImageSelector
            alt={`현대캐피탈 logo`}
            kiaLightSrc={() => import('@di-shared/assets/img/home/img_capital_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/img_capital.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/img_capital_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/img_capital.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/img_capital_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/img_capital.png')}
            width="106"
            height="20"
            className="logo"
          />
        </div>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            emitEventLog({
              event: 'di_start_asset_linking',
            });
            navigate(ROUTE_PATHS.CAPITAL_TERMS.getFullPath(vin));
          }}
        >
          {t('T40151')}
        </button>
      </div>
    </section>
  );
}

export default CapitalContents;
